/**
 * User Management Types for React Native Admin App
 * Based on admin-web user management implementation
 */

import { IFilterModel, ISortModel, IPaginationModel } from './dataTable';

// Re-export for convenience
export type { IFilterModel, ISortModel, IPaginationModel };

// API Types (matching admin-web)
export interface IApiRole {
  id: number;
  name: string;
  organizationId: number;
  propertyId?: number | null;
  createdAt: string;
  updatedAt: string;
  deletedAt?: string | null;
  organization?: {
    id: number;
    name: string;
    properties?: Array<{
      id: number;
      marketingName: string;
      organizationId: number;
    }>;
  };
  property?: {
    id: number;
    marketingName: string;
    organizationId: number;
  };
}

export interface IApiUser {
  id: number;
  email: string;
  firstName?: string;
  lastName?: string;
  isSuperUser?: boolean;
  organizations?: Array<{
    id: number;
    name: string;
  }>;
  properties?: Array<{
    id: number;
    marketingName: string;
    organizationId: number;
  }>;
  roles?: IApiRole[];
}

// API Response Types
export interface IUserManagementListResponse {
  success: boolean;
  response: IApiUser[];
}

// Table Row Types (for display)
export interface IUserManagementTableRow {
  id: number;
  name: string;
  userType: string;
  email: string;
  organization?: (string | null)[];
  properties?: (string | null)[];
}

// Form Types
export interface IUserManagementAccountForm {
  accountId?: number;
  email: string;
  firstName: string;
  lastName: string;
}

export interface IUserManagementRoleForm {
  email: string;
  orgIds: number[];
  propertyIds: number[];
  roleName: string;
}

export interface IUserRoleDelete {
  email: string;
  propertyId?: number;
  organizationId?: number;
  roleName?: string;
}

// Constants
export const UserRoleLabels = {
  propertyAdmin: "Property Admin",
  orgAdmin: "Organization Admin", 
  resioAdmin: "Resio Admin",
  billingAdmin: "Billing Admin",
};

export const roleNamesAndDescriptions = {
  superAdmin: {
    name: "Resio admin",
    value: "Resio Admin",
    description: "Full access and control over the entire system.",
  },
  orgAdmin: {
    name: "Organization admin", 
    value: "Organization Admin",
    description: "Access to all properties within a specific organization.",
  },
  propertyAdmin: {
    name: "Community manager",
    value: "Property Admin", 
    description: "Access to one or multiple properties within a specific organization.",
  },
  billingAdmin: {
    name: "Accounting admin",
    value: "Billing Admin",
    description: "Organization admin features enhanced with accounting capabilities.",
  },
};

export const userTypeOptions: string[] = [
  "Organization Admin",
  "Billing Admin", 
  "Property Admin",
  "Resio Admin",
];

// Table Keys (matching admin-web)
export enum UserManagementKeys {
  name = "name",
  userType = "userType", 
  email = "email",
  organization = "organization",
  properties = "properties",
}

// Hook return type
export interface IUseUserManagement {
  // Data
  data: IUserManagementListResponse;
  tableRows: IUserManagementTableRow[];
  totalFilteredCount: number;
  
  // State
  isLoading: boolean;
  error: string | null;
  
  // Enhanced Error Handling
  enhancedError: any;
  retryCount: number;
  canRetry: boolean;
  manualRetry: () => void;
  
  // Pagination
  paginationModel: IPaginationModel;
  updatePaginationModel: (model: IPaginationModel) => void;
  goToNextPage: () => void;
  goToPreviousPage: () => void;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  
  // Filtering & Sorting
  filterModel: IFilterModel;
  updateFilterModel: (model: IFilterModel) => void;
  sortModel: ISortModel[];
  updateSortModel: (model: ISortModel[]) => void;
  searchQuery: string;
  updateSearchQuery: (query: string) => void;
  
  // Actions
  refresh: () => void;
  getUserList: () => void;
}

// User Management Form Values (for future user creation/editing)
export interface IUserManagementFormValues {
  sortModel: ISortModel[];
  filterModel: IFilterModel;
  paginationModel: IPaginationModel;
  selectedPropertyId?: number;
  selectedOrganizationId?: number;
}
